import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Building2, Car, FileText, Gavel, Search, TrendingUp, User, Users } from 'lucide-react';
import { type DocumentSearchHit } from '../utils/documentSearchNavigation';
import { contactDetailPath, organizationDetailPath } from '../utils/contactRoutes';
import { leadDetailPath } from '../utils/leadRoutes';
import { employeeDetailPath } from '../utils/employeeRoutes';
import { fuhrparkVehiclePath } from '../utils/fuhrparkRoutes';
import { ausschreibungDetailPath } from '../utils/ausschreibungRoutes';
import {
  leadSelectPrimaryLabel,
  leadSelectSearchSecondaryLabel,
} from '../utils/leadSelectOption';
import {
  emptyEntitySearchResponse,
  flattenGlobalSearchHits,
  stepSearchIndex,
  type EntitySearchHit,
  type EntitySearchResponse,
  type GlobalSearchHit,
} from '../utils/entitySearchHits';
import { DocumentPreviewModal } from './DocumentPreviewModal';
import { InlineLoadingState } from './LoadingSpinner';

const SEARCH_SLICE = 5;
const SEARCH_DEBOUNCE_MS = 220;
const SECTION_LABEL_CLASS = 'text-xs font-normal text-app-text-subtle px-1 mb-1';

type Props = {
  instanceId: string;
};

function optionDomId(listboxId: string, key: string): string {
  return `${listboxId}-${key}`;
}

function SearchRow({
  active,
  optionId,
  icon,
  iconClass,
  title,
  subtitle,
  onHover,
  onClick,
  buttonRef,
}: {
  active: boolean;
  optionId: string;
  icon: React.ReactNode;
  iconClass: string;
  title: string;
  subtitle: string;
  onHover: () => void;
  onClick: () => void;
  buttonRef: (el: HTMLButtonElement | null) => void;
}) {
  return (
    <button
      ref={buttonRef}
      id={optionId}
      type="button"
      role="option"
      aria-selected={active}
      onMouseEnter={onHover}
      onClick={onClick}
      className={`w-full text-left px-2.5 py-1.5 rounded transition-colors ${
        active ? 'bg-app-surface-muted' : 'hover:bg-app-hover'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${iconClass}`}>
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-app-text truncate">{title}</div>
          {subtitle ? <div className="text-xs text-app-text-subtle truncate">{subtitle}</div> : null}
        </div>
      </div>
    </button>
  );
}

export function GlobalSearch({ instanceId }: Props) {
  const navigate = useNavigate();
  const searchWrapRef = React.useRef<HTMLDivElement | null>(null);
  const optionRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const listboxId = React.useId();
  const inputId = React.useId();
  const [query, setQuery] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingDocs, setIsLoadingDocs] = React.useState(false);
  const [entities, setEntities] = React.useState<EntitySearchResponse>(emptyEntitySearchResponse);
  const [documents, setDocuments] = React.useState<DocumentSearchHit[]>([]);
  const [previewDoc, setPreviewDoc] = React.useState<DocumentSearchHit | null>(null);
  const [dropdownRect, setDropdownRect] = React.useState<{ left: number; top: number; width: number } | null>(null);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const docsByKind = React.useMemo(() => {
    const byKind = (kind: DocumentSearchHit['kind']) =>
      documents.filter((d) => d.kind === kind).slice(0, SEARCH_SLICE);
    return {
      invoice: byKind('invoice'),
      lead_attachment: byKind('lead_attachment'),
      organization: byKind('organization'),
      ausschreibung: byKind('ausschreibung'),
    };
  }, [documents]);

  const hits = React.useMemo(
    () =>
      flattenGlobalSearchHits(entities, [
        ...docsByKind.invoice,
        ...docsByKind.lead_attachment,
        ...docsByKind.organization,
        ...docsByKind.ausschreibung,
      ]),
    [entities, docsByKind],
  );

  const hasResults = hits.length > 0;
  const activeHit = hits[activeIndex] ?? null;
  const activeDescendant =
    isOpen && activeHit ? optionDomId(listboxId, activeHit.key) : undefined;

  const goToHit = React.useCallback(
    (hit: GlobalSearchHit) => {
      if (hit.type === 'document') {
        setIsOpen(false);
        setPreviewDoc(hit.document);
        return;
      }
      setIsOpen(false);
      if (hit.type === 'project') {
        navigate(leadDetailPath({ id: hit.project.id }));
      } else if (hit.type === 'contact') {
        navigate(contactDetailPath(hit.id));
      } else if (hit.type === 'org') {
        navigate(organizationDetailPath(hit.id));
      } else if (hit.type === 'vehicle') {
        navigate(fuhrparkVehiclePath(instanceId, hit.id));
      } else if (hit.type === 'employee') {
        navigate(employeeDetailPath(instanceId, hit.id));
      } else if (hit.type === 'tender') {
        navigate(ausschreibungDetailPath({ id: hit.id, display_id: hit.display_id }));
      }
    },
    [instanceId, navigate],
  );

  React.useEffect(() => {
    if (!isOpen) return;
    const controller = new AbortController();
    setIsLoading(true);
    setIsLoadingDocs(true);
    const delay = query.trim() ? SEARCH_DEBOUNCE_MS : 0;
    const timer = window.setTimeout(async () => {
      const q = query.trim();
      try {
        const [entRes, docRes] = await Promise.all([
          fetch(`/api/search/entities?q=${encodeURIComponent(q)}&limit=${SEARCH_SLICE}`, {
            signal: controller.signal,
          }),
          fetch(`/api/search/documents?q=${encodeURIComponent(q)}&limit=${SEARCH_SLICE}`, {
            signal: controller.signal,
          }),
        ]);
        if (controller.signal.aborted) return;
        const entData = (entRes.ok ? await entRes.json() : emptyEntitySearchResponse()) as EntitySearchResponse;
        const docData = (docRes.ok ? await docRes.json() : { items: [] }) as { items?: DocumentSearchHit[] };
        setEntities({
          projects: Array.isArray(entData.projects) ? entData.projects : [],
          contacts: Array.isArray(entData.contacts) ? entData.contacts : [],
          organizations: Array.isArray(entData.organizations) ? entData.organizations : [],
          vehicles: Array.isArray(entData.vehicles) ? entData.vehicles : [],
          employees: Array.isArray(entData.employees) ? entData.employees : [],
          tenders: Array.isArray(entData.tenders) ? entData.tenders : [],
        });
        setDocuments(Array.isArray(docData.items) ? docData.items : []);
        setActiveIndex(0);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        console.error('Global search load failed:', error);
        setEntities(emptyEntitySearchResponse());
        setDocuments([]);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsLoadingDocs(false);
        }
      }
    }, delay);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [isOpen, query]);

  React.useEffect(() => {
    if (activeIndex >= hits.length) setActiveIndex(0);
  }, [activeIndex, hits.length]);

  React.useEffect(() => {
    optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, hits]);

  React.useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    const updateRect = () => {
      if (!searchWrapRef.current) return;
      const rect = searchWrapRef.current.getBoundingClientRect();
      setDropdownRect({
        left: rect.left,
        top: rect.bottom + 8,
        width: rect.width,
      });
    };
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [isOpen]);

  const onInputKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && (ev.key === 'ArrowDown' || ev.key === 'ArrowUp')) {
      setIsOpen(true);
      return;
    }
    if (ev.key === 'Escape') {
      ev.preventDefault();
      setIsOpen(false);
      return;
    }
    if (!isOpen || hits.length === 0) return;
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setActiveIndex((i) => stepSearchIndex(i, 1, hits.length));
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setActiveIndex((i) => stepSearchIndex(i, -1, hits.length));
    } else if (ev.key === 'Enter' && activeHit) {
      ev.preventDefault();
      goToHit(activeHit);
    }
  };

  const hitIndex = React.useMemo(() => {
    const map = new Map<string, number>();
    hits.forEach((hit, index) => map.set(hit.key, index));
    return map;
  }, [hits]);

  const renderEntitySection = (
    label: string,
    rows: EntitySearchHit[],
    type: 'contact' | 'org' | 'vehicle' | 'employee' | 'tender',
    icon: React.ReactNode,
    iconClass: string,
  ) => {
    if (rows.length === 0) return null;
    return (
      <div>
        <p className={SECTION_LABEL_CLASS}>{label}</p>
        <div className="space-y-1">
          {rows.map((row) => {
            const key = type === 'tender' ? `tender-${row.id}` : `${type === 'org' ? 'org' : type}-${row.id}`;
            const index = hitIndex.get(key) ?? -1;
            const hit = hits[index];
            if (!hit) return null;
            return (
              <SearchRow
                key={key}
                optionId={optionDomId(listboxId, key)}
                active={index === activeIndex}
                icon={icon}
                iconClass={iconClass}
                title={row.title}
                subtitle={row.subtitle}
                onHover={() => setActiveIndex(index)}
                onClick={() => goToHit(hit)}
                buttonRef={(el) => {
                  optionRefs.current[index] = el;
                }}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const renderDocSection = (label: string, rows: DocumentSearchHit[], iconClass: string) => {
    if (rows.length === 0) return null;
    return (
      <div>
        <p className={SECTION_LABEL_CLASS}>{label}</p>
        <div className="space-y-1">
          {rows.map((doc) => {
            const key = `document-${doc.kind}-${doc.id}`;
            const index = hitIndex.get(key) ?? -1;
            const hit = hits[index];
            if (!hit) return null;
            return (
              <SearchRow
                key={key}
                optionId={optionDomId(listboxId, key)}
                active={index === activeIndex}
                icon={<FileText className="w-3.5 h-3.5" />}
                iconClass={iconClass}
                title={doc.filename}
                subtitle={doc.subtitle}
                onHover={() => setActiveIndex(index)}
                onClick={() => goToHit(hit)}
                buttonRef={(el) => {
                  optionRefs.current[index] = el;
                }}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const searchField = (
    <div
      className={`pointer-events-auto fixed left-1/2 top-7 hidden w-[480px] -translate-x-1/2 -translate-y-1/2 md:block ${
        isOpen ? 'z-[240]' : 'z-[25]'
      }`}
    >
      <div ref={searchWrapRef} className="relative rounded-full bg-app-surface">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          id={inputId}
          type="text"
          role="combobox"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeDescendant}
          placeholder="Meisterportal durchsuchen"
          className="w-full rounded-full border border-app-border bg-app-surface py-1.5 pl-10 pr-4 text-sm text-app-text outline-none focus:border-app-border focus:bg-app-surface focus:ring-0 [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_var(--color-app-surface)] [&:-webkit-autofill]:[-webkit-text-fill-color:var(--color-app-text)]"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onClick={() => setIsOpen(true)}
          onKeyDown={onInputKeyDown}
        />
      </div>
    </div>
  );

  return (
    <>
      {createPortal(searchField, document.body)}

      {isOpen &&
        dropdownRect &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[220] bg-black/25" onClick={() => setIsOpen(false)} />
            <div
              id={listboxId}
              role="listbox"
              aria-label="Suchergebnisse"
              className="fixed bg-app-surface rounded-md shadow-2xl border border-app-border overflow-hidden z-[230]"
              style={{ left: dropdownRect.left, top: dropdownRect.top, width: dropdownRect.width }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="app-scrollbar max-h-[50vh] overflow-y-auto overscroll-contain p-2">
                <h3 className="text-sm font-semibold text-app-text-muted mb-2 px-1">
                  {query.trim() ? 'Suchergebnisse' : 'Vorschläge'}
                </h3>
                {isLoading && !hasResults ? (
                  <InlineLoadingState label="Lade …" className="px-2 py-4" />
                ) : !hasResults && !isLoadingDocs ? (
                  <p className="text-sm text-app-text-subtle px-2 py-4">Keine Treffer.</p>
                ) : (
                  <div className="space-y-3">
                    {entities.projects.length > 0 && (
                      <div>
                        <p className={SECTION_LABEL_CLASS}>
                          Projekte
                        </p>
                        <div className="space-y-1">
                          {entities.projects.map((lead) => {
                            const key = `project-${lead.id}`;
                            const index = hitIndex.get(key) ?? -1;
                            const hit = hits[index];
                            if (!hit) return null;
                            return (
                              <SearchRow
                                key={key}
                                optionId={optionDomId(listboxId, key)}
                                active={index === activeIndex}
                                icon={
                                  lead.name?.charAt(0)?.toUpperCase() || (
                                    <TrendingUp className="w-3.5 h-3.5" />
                                  )
                                }
                                iconClass="bg-blue-100 text-blue-700 text-xs"
                                title={leadSelectPrimaryLabel(lead)}
                                subtitle={leadSelectSearchSecondaryLabel(lead)}
                                onHover={() => setActiveIndex(index)}
                                onClick={() => goToHit(hit)}
                                buttonRef={(el) => {
                                  optionRefs.current[index] = el;
                                }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {renderEntitySection(
                      'Kontakte',
                      entities.contacts,
                      'contact',
                      <User className="w-3.5 h-3.5" />,
                      'bg-emerald-100 text-emerald-800',
                    )}
                    {renderEntitySection(
                      'Organisationen',
                      entities.organizations,
                      'org',
                      <Building2 className="w-3.5 h-3.5" />,
                      'bg-blue-100 text-blue-700',
                    )}
                    {renderEntitySection(
                      'Fahrzeuge',
                      entities.vehicles,
                      'vehicle',
                      <Car className="w-3.5 h-3.5" />,
                      'bg-orange-100 text-orange-700',
                    )}
                    {renderEntitySection(
                      'Mitarbeiter',
                      entities.employees,
                      'employee',
                      <Users className="w-3.5 h-3.5" />,
                      'bg-app-surface-muted text-app-text-muted',
                    )}
                    {renderEntitySection(
                      'Ausschreibungen',
                      entities.tenders,
                      'tender',
                      <Gavel className="w-3.5 h-3.5" />,
                      'bg-purple-100 text-purple-700',
                    )}
                    {renderDocSection('Angebote & Rechnungen', docsByKind.invoice, 'bg-amber-100 text-amber-700')}
                    {renderDocSection('Anhänge (Projekte)', docsByKind.lead_attachment, 'bg-app-surface-muted text-app-text-muted')}
                    {renderDocSection('Organisationsdateien', docsByKind.organization, 'bg-blue-100 text-blue-700')}
                    {renderDocSection(
                      'Ausschreibungsunterlagen',
                      docsByKind.ausschreibung,
                      'bg-purple-100 text-purple-700',
                    )}
                    {isLoadingDocs && (
                      <InlineLoadingState label="Dokumente werden geladen …" className="px-2 py-1 text-xs" />
                    )}
                  </div>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}

      <DocumentPreviewModal document={previewDoc} onClose={() => setPreviewDoc(null)} />
    </>
  );
}
